import React, { useEffect, useState } from "react";
import "../assets/styles/PhoneVideo.scss";

type Props = {
  /** MP4 of the app screen only (no bezel): the frame is drawn in CSS. */
  src: string;
  poster: string;
  /** Short description of what the clip shows, for screen readers. */
  label: string;
  /** Muted, looping, autoplaying (hero). Skipped when the visitor prefers reduced motion. */
  autoplay?: boolean;
  /** Optional line under the phone. */
  caption?: string;
  /** "small" tightens the case's corner radius and bezel for phones under ~240px. */
  size?: "default" | "small";
  className?: string;
};

/**
 * App footage inside a CSS phone frame. Autoplay clips are muted loops with
 * no controls; everything else is click-to-play with the poster shown first
 * (`preload="metadata"` keeps the page light until someone presses play).
 */
const PhoneVideo: React.FC<Props> = ({ src, poster, label, autoplay = false, caption, size = "default", className }) => {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const loop = autoplay && !reducedMotion;

  return (
    <figure className={`phone-video${size === "small" ? " phone-video--small" : ""}${className ? ` ${className}` : ""}`}>
      <div className="phone-video-frame">
        <video
          className="phone-video-screen"
          src={src}
          poster={poster}
          aria-label={label}
          autoPlay={loop}
          loop={loop}
          muted={loop}
          playsInline
          controls={!loop}
          preload={loop ? "auto" : "metadata"}
        />
      </div>
      {caption && <figcaption className="phone-video-caption">{caption}</figcaption>}
    </figure>
  );
};

export default PhoneVideo;
